import type { Project } from "@nlc/shared-types";

import { SubscriptionStatusEnum } from "@nlc/shared-types";
import { useMemo } from "react";

import { Link } from "@/components/link";
import { env } from "@/config/env";
import { useUser } from "@/hooks/useUser.tsx";
import { dateFormatter } from "@/utils/dateFormatter.ts";

type AccountStatusBanner = {
  action?: React.ReactNode;
  message: React.ReactNode;
  title: string;
};

export function useShowBanners({
  project,
  skipDunning = false,
}: {
  project?: Pick<
    Project,
    "isFirstDraft" | "nextBillingDate" | "subscriptionStatus"
  >;
  skipDunning?: boolean;
} = {}) {
  const user = useUser();
  const targetProject = project ?? user?.currentProject;
  const projectInfo = useMemo(
    () => ({
      nextBillingDate: targetProject?.nextBillingDate,
      paymentFailure: targetProject?.subscriptionStatus?.paymentFailureReason,
      subscriptionStatus: targetProject?.subscriptionStatus.subscriptionStatus,
    }),
    [targetProject],
  );

  function BannerMessage({ children }: { children: React.ReactNode }) {
    return <p className="text-sm text-text-tertiary">{children}</p>;
  }

  function ReactivateAction() {
    return (
      <Link
        className="text-sm font-semibold text-button-tertiary-color-fg hover:text-button-tertiary-color-fg-hover sm:text-sm"
        href="https://nlc.com/manage-my-account/"
      >
        Reactivate subscription
      </Link>
    );
  }

  function CanceledMessage() {
    return (
      <BannerMessage>
        Contact us to reactivate your design subscription and start submitting
        new design requests.
      </BannerMessage>
    );
  }

  function PendingCancellationMessage({
    nextBillingDate,
  }: {
    nextBillingDate: Date | undefined;
  }) {
    return (
      <BannerMessage>
        You can continue to use the service and submit new design requests until{" "}
        {dateFormatter(nextBillingDate)}. Contact us to reactivate your design
        subscription.
      </BannerMessage>
    );
  }

  const banners = useMemo(
    () => ({
      canceled: {
        admin: {
          message: <CanceledMessage />,
          title: "We're going to miss you! Your subscription is now canceled.",
        },
        collaborator: {
          message: <CanceledMessage />,
          title: "We're going to miss you! Your subscription is now canceled.",
        },
        owner: {
          action: <ReactivateAction />,
          message: (
            <BannerMessage>
              Reactivate your design subscription to start submitting new design
              requests.
            </BannerMessage>
          ),
          title: "We're going to miss you! Your subscription is now canceled.",
        },
      },
      dunningProcess: {
        admin: {
          message: (
            <BannerMessage>
              {projectInfo.paymentFailure
                ? `Your payment failed due to: ${projectInfo.paymentFailure}`
                : "There was a problem with your payment"}
              . Please contact the owner of your account to ensure uninterrupted
              access to your projects.
            </BannerMessage>
          ),
          title: "Whoops, your subscription failed to renew.",
        },
        owner: {
          message: (
            <BannerMessage>
              {projectInfo.paymentFailure
                ? `Your payment failed due to: ${projectInfo.paymentFailure}`
                : "There was a problem with your payment"}
              . Please{" "}
              <Link
                className="text-text-brand-primary underline text-sm font-semibold"
                href={`${env.VITE_BILLING_URL}${env.VITE_STRIPE_BILLING_TOKEN}?prefilled_email=${encodeURIComponent(user?.data?.user?.email || "")}`}
              >
                update your payment method
              </Link>{" "}
              to ensure uninterrupted access to your projects.
            </BannerMessage>
          ),
          title: "Whoops, your subscription failed to renew.",
        },
      },
      pendingCancellation: {
        admin: {
          message: (
            <PendingCancellationMessage
              nextBillingDate={projectInfo?.nextBillingDate}
            />
          ),
          title: `Uh-oh, you canceled your subscription.`,
        },
        collaborator: {
          message: (
            <PendingCancellationMessage
              nextBillingDate={projectInfo?.nextBillingDate}
            />
          ),
          title: `Uh-oh, you canceled your subscription.`,
        },
        owner: {
          action: <ReactivateAction />,
          message: (
            <BannerMessage>
              You can continue to use the service and submit new design requests
              until {dateFormatter(projectInfo?.nextBillingDate)} or reactivate
              your design subscription.
            </BannerMessage>
          ),
          title: `Uh-oh, you canceled your subscription.`,
        },
      },
    }),
    [projectInfo],
  );

  return useMemo<AccountStatusBanner | null | undefined>(() => {
    const userRole = user?.data?.user?.roleName as
      | "admin"
      | "collaborator"
      | "owner";

    switch (projectInfo.subscriptionStatus) {
      case SubscriptionStatusEnum.DUNNING_PROCESS: {
        if (skipDunning) return null;

        return banners.dunningProcess[userRole as "admin" | "owner"];
      }
      case SubscriptionStatusEnum.PENDING_CANCELLATION: {
        if (targetProject?.isFirstDraft) return null;

        return banners.pendingCancellation[userRole];
      }
      case SubscriptionStatusEnum.CANCELED: {
        if (targetProject?.isFirstDraft) return null;

        return banners.canceled[userRole];
      }
      default: {
        return null;
      }
    }
  }, [projectInfo, banners, user, targetProject, skipDunning]);
}
